/**
 * Dual-normal — GLSL MultiplMapShader Rodrigues (WebGPU / TSL).
 *
 * Call after base is on `userData.baseNormalMap` and fabric is on `normalMap`
 * (SugarPart.apply / SugarMaterial.apply). WebGPU caches `normalNode` as "{}",
 * so customProgramCacheKey + needsUpdate are required.
 */
import {
  Material,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Texture,
  Vector2,
} from "three";
import { Fn, TBNViewMatrix, float, texture, uv, vec2, vec3 } from "three/tsl";

type DualNormalMaterial = (MeshStandardMaterial | MeshPhysicalMaterial) & {
  normalNode?: unknown | null;
  customProgramCacheKey?: () => string;
};

function createDualNormalNode(
  fabricNormalMap: Texture,
  baseNormalMap: Texture,
  normalScale: Vector2,
): unknown {
  const scale = vec2(normalScale.x, normalScale.y);

  return Fn(() => {
    const mapN = texture(fabricNormalMap).rgb.mul(2).sub(1).toVar();
    // GLSL: vUvBase = uv (no matrix)
    const baseNormal = texture(baseNormalMap, uv()).rgb.mul(2).sub(1);

    const source = vec3(0.0, 0.0, 1.0);
    const v = baseNormal.cross(source);
    const c = source.dot(baseNormal);
    const k = float(1.0).div(float(1.0).add(c));

    // GLSL mat3(...) = [-v]_x → mapN - v×mapN + v×(v×mapN)*k
    mapN.assign(
      mapN.sub(v.cross(mapN)).add(v.cross(v.cross(mapN)).mul(k)),
    );
    mapN.assign(vec3(mapN.xy.mul(scale), mapN.z));

    return (
      TBNViewMatrix as unknown as {
        mul: (n: unknown) => { normalize: () => unknown };
      }
    )
      .mul(mapN)
      .normalize();
  })();
}

function dualNormalCacheKey(material: DualNormalMaterial): string {
  const base = material.userData?.baseNormalMap as Texture | undefined;
  const fabric = material.normalMap ?? undefined;
  return [
    "dual-normal",
    base?.uuid ?? "",
    fabric?.uuid ?? "",
    material.normalNode ? "1" : "0",
    material.normalScale?.x ?? 1,
    material.normalScale?.y ?? 1,
  ].join(":");
}

function touchMaterial(m: DualNormalMaterial): void {
  m.customProgramCacheKey = () => dualNormalCacheKey(m);
  m.needsUpdate = true;
}

/** Call after base is on userData and fabric is on normalMap. */
export function refreshDualNormal(material: Material): void {
  const m = material as DualNormalMaterial;
  const base = material.userData?.baseNormalMap as Texture | undefined;
  const fabric = m.normalMap as Texture | undefined;

  if (!base || !base.isTexture) {
    m.normalNode = null;
    touchMaterial(m);
    return;
  }

  if (
    !fabric ||
    !fabric.isTexture ||
    fabric === base ||
    fabric.uuid === base.uuid
  ) {
    if (!fabric || !fabric.isTexture) m.normalMap = base;
    m.normalNode = null;
    touchMaterial(m);
    return;
  }

  m.normalNode = createDualNormalNode(
    fabric,
    base,
    m.normalScale ?? new Vector2(1, 1),
  ) as DualNormalMaterial["normalNode"];
  touchMaterial(m);
}
